/**
 * Главная §64.
 *
 * Поле промпта — главный элемент экрана. Блок персоны показывает статусы §5.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { projectStatusLabels, type ProjectStatus } from '@alterium/shared';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { StatusDot } from '../components/ui/StatusDot';
import { projectsApi } from '../api/projects';
import { personaApi } from '../api/persona';
import { avatarApi } from '../api/avatar';
import { voiceApi } from '../api/voice';
import { useAuthStore } from '../stores/auth';
import { useTranslation } from 'react-i18next';

/** Ключ приветствия по времени суток. Перевод подставляет компонент. */
function greetingKey(date = new Date()): string {
  const hour = date.getHours();
  if (hour < 5) return 'home.greeting.night';
  if (hour < 12) return 'home.greeting.morning';
  if (hour < 18) return 'home.greeting.day';
  return 'home.greeting.evening';
}

export function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [prompt, setPrompt] = useState('');

  const projects = useQuery({ queryKey: ['projects'], queryFn: projectsApi.list });
  const persona = useQuery({ queryKey: ['persona'], queryFn: personaApi.get });
  const avatar = useQuery({ queryKey: ['avatar'], queryFn: avatarApi.get });
  const voice = useQuery({ queryKey: ['voice'], queryFn: voiceApi.get });

  const recent = (projects.data ?? []).slice(0, 3);
  const hasPersona = Boolean(persona.data);

  // §5: три части персоны — по одному источнику на каждую, без домыслов.
  const personaParts = [
    { label: t('persona.part.avatar'), ready: avatar.data?.status === 'ready' },
    { label: t('persona.part.voice'), ready: voice.data?.status === 'ready' },
    { label: t('persona.part.appearance'), ready: (persona.data?.appearanceCount ?? 0) > 0 },
  ];

  return (
    <div className="flex flex-col gap-10">
      <div>
        <p className="text-body text-content-secondary">
          {t(greetingKey())}
          {user?.name ? `, ${user.name}` : ''}
        </p>
        <h1 className="mt-1 text-page-title font-semibold">{t('home.title')}</h1>
      </div>

      <Card>
        <CardBody className="flex flex-col gap-4">
          <textarea
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            rows={4}
            placeholder={t('home.promptPlaceholder')}
            className="w-full resize-none bg-transparent text-body text-content placeholder:text-content-muted focus:outline-none"
          />
          <div className="flex justify-end">
            <Button
              variant="primary"
              disabled={prompt.trim().length === 0}
              onClick={() => navigate('/create', { state: { prompt } })}
            >{t('common.create')}<ArrowRight className="h-4 w-4" strokeWidth={2} />
            </Button>
          </div>
        </CardBody>
      </Card>

      <section>
        <h2 className="mb-4 text-section-title font-medium">{t('home.recentProjects')}</h2>
        {recent.length === 0 ? (
          <EmptyState
            title={t('home.empty.title')}
            description={t('projects.empty.description')}
          />
        ) : (
          <div className="flex flex-col gap-2">
            {recent.map((project) => (
              <button
                key={project.id}
                type="button"
                onClick={() => navigate(`/projects/${project.id}`)}
                className="flex items-center justify-between rounded-card border border-line bg-surface px-4 py-3 text-left transition-colors duration-fast hover:border-line-strong"
              >
                <span className="text-body text-content">{project.title}</span>
                <span className="text-meta text-content-muted">
                  {projectStatusLabels[project.status as ProjectStatus] ?? project.status}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-4 text-section-title font-medium">{t('persona.title')}</h2>
        <Card>
          <CardBody className="flex flex-wrap items-center gap-x-8 gap-y-3">
            {personaParts.map((part) => (
              <div key={part.label} className="flex items-center gap-2 text-body">
                <StatusDot tone={part.ready ? 'ok' : 'idle'} />
                <span className="text-content">{part.label}</span>
                <span className="text-content-muted">{part.ready ? t('common.ready') : t('common.notCreated')}</span>
              </div>
            ))}
            <Button className="ml-auto" onClick={() => navigate('/persona')}>
              {hasPersona ? t('home.openPersona') : t('home.createPersona')}
            </Button>
          </CardBody>
        </Card>
      </section>
    </div>
  );
}
